/**
 * Generates a detailed table of cumulative ETF fills.
 * @param {Array} etfs - An array of ETF objects. Each ETF object must include:
 *   - symbol: The ETF ticker symbol.
 *   - weight: Weight of the ETF.
 *   - holdings: Array of holdings, each holding must include:
 *     - symbol: Stock symbol.
 *     - wgt: Weight of the stock in the ETF.
 * @returns {string[][]} detailedRows - The detailed table rows as an array of arrays of strings.
 */
function formatCumulativeEtfHoldings(etfs) {
    const etfSymbols = etfs.map((etf) => etf.symbol);
    const { etfContributions, etfWeights } = calculateEtfContributions(etfs);
    const consolidatedStocks = consolidateStocks(etfs, etfContributions, etfWeights);
    const sortedStocks = Object.entries(consolidatedStocks).sort(
        (a, b) => b[1].totalWeight - a[1].totalWeight
    );
    const detailedRows = buildDetailedRows(sortedStocks, etfSymbols, etfWeights);
    return detailedRows;
}

function calculateEtfContributions(etfs) {
    if (!etfs?.length) return { etfContributions: {}, etfWeights: {} };
    const etfContributions = {};
    const etfWeights = {};

    etfs.forEach((etf) => {
        const etfSymbol = etf.symbol;
        etfWeights[etfSymbol] = etf.weight;
        const totalHoldingsWeight = etf.holdings?.reduce((sum, holding) => sum + holding.wgt, 0) ?? 0;
        etfContributions[etfSymbol] = totalHoldingsWeight;
    });

    return { etfContributions, etfWeights };
}

function consolidateStocks(etfs, etfContributions, etfWeights) {
    if (!etfs?.length) return {};
    const consolidatedStocks = {};

    etfs.forEach((etf) => {
        const etfSymbol = etf.symbol;
        const etfWeight = etf.weight;

        etf.holdings.forEach((holding) => {
            const symbol = holding.symbol;
            const holdingWeight = holding.wgt;

            if (!consolidatedStocks[symbol]) {
                consolidatedStocks[symbol] = { totalWeight: 0, contributions: {} };
            }

            const absoluteWeight = holdingWeight / etfContributions[etfSymbol];
            const relativeWeight = absoluteWeight * etfWeight;
            consolidatedStocks[symbol].totalWeight += relativeWeight;

            if (!consolidatedStocks[symbol].contributions[etfSymbol]) {
                consolidatedStocks[symbol].contributions[etfSymbol] = { absolute: 0, relative: 0 };
            }

            consolidatedStocks[symbol].contributions[etfSymbol].absolute += absoluteWeight;
            consolidatedStocks[symbol].contributions[etfSymbol].relative += relativeWeight;
        });
    });

    return consolidatedStocks;
}

function buildDetailedRows(sortedStocks, etfSymbols, etfWeights) {
    const headers = ['Ticker', 'Weight', 'Total'];
    etfSymbols.forEach((etfSymbol) => headers.push(`${etfSymbol} Abs`, `${etfSymbol} Rel`));
    const detailedRows = [headers];
    const etfCumulativeWeights = {};

    etfSymbols.forEach((etfSymbol) => {
        etfCumulativeWeights[etfSymbol] = { absolute: 0, relative: 0 };
    });

    let cumulativeWeight = 0;

    sortedStocks.forEach(([symbol, data]) => {
        cumulativeWeight += data.totalWeight;
        const row = [symbol, data.totalWeight.toFixed(4), cumulativeWeight.toFixed(4)];

        etfSymbols.forEach((etfSymbol) => {
            const contributions = data.contributions[etfSymbol] || { absolute: 0, relative: 0 };
            etfCumulativeWeights[etfSymbol].absolute += contributions.absolute;
            etfCumulativeWeights[etfSymbol].relative += contributions.relative;

            row.push(
                etfCumulativeWeights[etfSymbol].absolute.toFixed(4),
                etfCumulativeWeights[etfSymbol].relative.toFixed(4)
            );
        });

        detailedRows.push(row);
    });

    return detailedRows;
}

export default formatCumulativeEtfHoldings;
